import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  MenuItem,
  Paper,
  IconButton,
  Autocomplete,
  Popover,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Icon } from "../../icons";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { format, parseISO, isValid } from "date-fns";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import { useNavigate } from "react-router-dom";
import ClientNotificationBanner from "../../components/ClientNotificationBanner";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    "& fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.PRIMARY,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
  "& .MuiInputBase-input": {
    color: COLORS.PRIMARY,
  },
};

const dateInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    "& fieldset": {
      borderColor: `${COLORS.PRIMARY} !important`,
    },
    "&:hover fieldset": {
      borderColor: `${COLORS.PRIMARY} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: `${COLORS.PRIMARY} !important`,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${COLORS.PRIMARY} !important`,
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
  "& .MuiInputBase-input": {
    color: COLORS.PRIMARY,
  },
  "& .MuiSvgIcon-root": {
    color: COLORS.PRIMARY,
  },
};

const AddSiteCheck = () => {
  const { token, userData, isAuthenticated, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const [instructions, setInstructions] = useState([]);
  const [currentInstruction, setCurrentInstruction] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [drawingFile, setDrawingFile] = useState(null);
  const [drawingNumber, setDrawingNumber] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();

  const [nameOfProject, setNameOfProject] = useState("");
  const [detailedAddress, setDetailedAddress] = useState("");
  const [nameOfClient, setNameOfClient] = useState("");
  const [phoneOfClient, setPhoneOfClient] = useState("");
  const [useMyDetails, setUseMyDetails] = useState(false);
  const [isOutstation, setIsOutstation] = useState(false);

  const [originalUserDetails, setOriginalUserDetails] = useState({
    name: "",
    phone: "",
  });

  const [purchaseToast, setPurchaseToast] = useState(null);

  useEffect(() => {
    try {
      const dataStr = localStorage.getItem("purchase_success");
      if (dataStr) {
        const data = JSON.parse(dataStr);
        if (Date.now() - data.timestamp < 60000) {
          setPurchaseToast(data);
        }
        localStorage.removeItem("purchase_success");
      }
    } catch (e) {
      console.error("Failed to parse purchase success data:", e);
    }
  }, []);
  const [userEnteredDetails, setUserEnteredDetails] = useState({
    name: "",
    phone: "",
  });

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);

  const { loading: authLoading } = useAuth();

  useEffect(() => {
    fetchChecklistTemplates();

    if (userData) {
      setOriginalUserDetails({
        name: userData.name || "",
        phone: userData.phone || "",
      });
    }
  }, [userData]);

  const draftKey = "draft_add_site_check";

  // Load draft on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        if (parsed.nameOfProject) setNameOfProject(parsed.nameOfProject);
        if (parsed.detailedAddress) setDetailedAddress(parsed.detailedAddress);
        if (parsed.nameOfClient) setNameOfClient(parsed.nameOfClient);
        if (parsed.phoneOfClient) setPhoneOfClient(parsed.phoneOfClient);
        if (parsed.useMyDetails !== undefined) setUseMyDetails(parsed.useMyDetails);
        if (parsed.isOutstation !== undefined) setIsOutstation(parsed.isOutstation);
        if (parsed.dateRange) setDateRange(parsed.dateRange);
        if (parsed.additionalDetails) setAdditionalDetails(parsed.additionalDetails);
        if (parsed.selectedSite) setSelectedSite(parsed.selectedSite);
        if (parsed.instructions) setInstructions(parsed.instructions);
        if (parsed.drawingNumber) setDrawingNumber(parsed.drawingNumber);
      }
    } catch (e) {
      console.error("Failed to parse draft data", e);
    }
  }, []);

  // Save draft on change
  useEffect(() => {
    const draftData = {
      nameOfProject,
      detailedAddress,
      nameOfClient,
      phoneOfClient,
      useMyDetails,
      isOutstation,
      dateRange,
      additionalDetails,
      selectedSite,
      instructions,
      drawingNumber
    };
    try {
      localStorage.setItem(draftKey, JSON.stringify(draftData));
    } catch (e) {
      console.error("Failed to save draft data", e);
    }
  }, [nameOfProject, detailedAddress, nameOfClient, phoneOfClient, useMyDetails, isOutstation, dateRange, additionalDetails, selectedSite, instructions, drawingNumber]);

  const fetchChecklistTemplates = async () => {
    try {
      setTemplatesLoading(true);

      if (!token) {
        showToast(
          "error",
          "Authentication Error",
          "Authentication token not found. Please login again."
        );
        setTemplatesLoading(false);
        return;
      }

      const response = await axios.get(`${url}/api/admin/checklist-templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          isActive: true,
          limit: 100,
        },
      });

      if (response.data && response.data.data) {
        setTemplates(response.data.data);

        const options = response.data.data
          .filter((template) => template.isActive !== false)
          .map((template) => template.title);

        setTemplateOptions(options);
      } else {
        setTemplates([]);
        setTemplateOptions([]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      showToast(
        "error",
        "Loading Error",
        "Failed to load checklist templates. Please try again."
      );

      const defaultOptions = [
        "Blockwork",
        "Internal Plaster",
        "External Plaster",
        "Plumbing",
        "Paint",
      ];
      setTemplateOptions(defaultOptions);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const showToast = (type, title, message) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastOpen(true);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const handleAddInstruction = () => {
    if (!currentInstruction.trim()) return;
    setInstructions([...instructions, currentInstruction]);
    setCurrentInstruction("");
  };

  const handleRemoveInstruction = (index) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const handleUseMyDetailsChange = (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setUserEnteredDetails({
        name: nameOfClient,
        phone: phoneOfClient,
      });

      setNameOfClient(originalUserDetails.name);
      setPhoneOfClient(originalUserDetails.phone);
    } else {
      setNameOfClient(userEnteredDetails.name);
      setPhoneOfClient(userEnteredDetails.phone);
    }

    setUseMyDetails(isChecked);
  };

  const handleDrawingFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== "application/pdf") {
        showToast(
          "error",
          "Invalid File Type",
          "Please upload PDF files only."
        );
        return;
      }

      setDrawingFile(file);
    }
  };

  const handleRemoveDrawing = () => {
    setDrawingFile(null);
    setDrawingNumber("");
  };

  const handleCalendarClick = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date && endDate) {
      const formattedStart = format(date, "yyyy-MM-dd");
      const formattedEnd = format(endDate, "yyyy-MM-dd");
      setDateRange(`${formattedStart} to ${formattedEnd}`);
    } else if (date) {
      const formattedStart = format(date, "yyyy-MM-dd");
      setDateRange(`${formattedStart}`);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (startDate && date) {
      const formattedStart = format(startDate, "yyyy-MM-dd");
      const formattedEnd = format(date, "yyyy-MM-dd");
      setDateRange(`${formattedStart} to ${formattedEnd}`);
    }
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);

    const parts = value.split(" to ");
    if (parts.length === 2) {
      const start = parseISO(parts[0].trim());
      const end = parseISO(parts[1].trim());

      if (isValid(start)) setStartDate(start);
      if (isValid(end)) setEndDate(end);
    } else if (parts.length === 1) {
      const start = parseISO(parts[0].trim());
      if (isValid(start)) setStartDate(start);
    }
  };

  const handleClearDates = () => {
    setDateRange("");
    setStartDate(null);
    setEndDate(null);
  };

  // Parse date range to proper format for backend
  const parseDateRange = () => {
    if (!dateRange.trim()) return null;

    const parts = dateRange.split(" to ");

    if (parts.length === 1) {
      const date = parseISO(parts[0].trim());
      if (isValid(date)) {
        return {
          fromDate: format(date, "yyyy-MM-dd"),
          toDate: format(date, "yyyy-MM-dd"),
        };
      }
    } else if (parts.length === 2) {
      const startDate = parseISO(parts[0].trim());
      const endDate = parseISO(parts[1].trim());

      if (isValid(startDate)) {
        const dateObj = {
          fromDate: format(startDate, "yyyy-MM-dd"),
        };

        if (isValid(endDate)) {
          dateObj.toDate = format(endDate, "yyyy-MM-dd");
        } else {
          // If end date is invalid, use start date as end date
          dateObj.toDate = format(startDate, "yyyy-MM-dd");
        }

        return dateObj;
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    // Check authentication first
    if (!isAuthenticated || !userData) {
      showToast(
        "error",
        "Authentication Required",
        "You must be logged in to submit a site check request."
      );
      return;
    }

    // Validation
    if (!nameOfProject.trim()) {
      showToast("error", "Validation Error", "Please enter Name of Project");
      return;
    }
    if (!detailedAddress.trim()) {
      showToast("error", "Validation Error", "Please enter Detailed Address");
      return;
    }
    if (!nameOfClient.trim()) {
      showToast(
        "error",
        "Validation Error",
        "Please enter Name of Contact Person"
      );
      return;
    }
    if (!phoneOfClient.trim()) {
      showToast(
        "error",
        "Validation Error",
        "Please enter Phone Number of POC"
      );
      return;
    }
    if (!dateRange.trim()) {
      showToast(
        "error",
        "Validation Error",
        "Please select Site Visit Date Range"
      );
      return;
    }
    if (!selectedSite) {
      showToast("error", "Validation Error", "Please select a Site Check");
      return;
    }
    if (!drawingFile) {
      showToast("error", "Validation Error", "Please upload a drawing PDF");
      return;
    }
    if (!drawingNumber.trim()) {
      showToast("error", "Validation Error", "Please enter Drawing Number");
      return;
    }

    // Parse date range
    const parsedDateRange = parseDateRange();
    if (!parsedDateRange || !parsedDateRange.fromDate) {
      showToast("error", "Validation Error", "Please enter a valid date range");
      return;
    }

    // Find template ID based on selected site title
    const selectedTemplate = templates.find(
      (template) => template.title === selectedSite
    );
    if (!selectedTemplate) {
      showToast(
        "error",
        "Template Error",
        "Selected site checklist template not found"
      );
      return;
    }

    // Validate template is active
    if (selectedTemplate.isActive === false) {
      showToast(
        "error",
        "Template Error",
        "Selected checklist template is not active"
      );
      return;
    }

    setLoading(true);

    try {
      // Prepare form data for file upload
      const formData = new FormData();

      // Add text fields
      formData.append("templateID", selectedTemplate._id);
      formData.append("nameOfProject", nameOfProject);
      formData.append("detailedAddress", detailedAddress);
      formData.append("nameOfClient", nameOfClient);
      formData.append("phoneOfClient", phoneOfClient);
      formData.append("isOutstation", isOutstation);

      // Send preferredSiteVisitDate as JSON string with date strings
      const dateString = parsedDateRange.fromDate;
      const toDateString = parsedDateRange.toDate || parsedDateRange.fromDate;

      formData.append(
        "preferredSiteVisitDate",
        JSON.stringify({
          fromDate: dateString,
          toDate: toDateString,
        })
      );

      formData.append("additionalTime", additionalDetails);

      // Send specificInstructions as JSON string array
      formData.append("specificInstructions", JSON.stringify(instructions));

      formData.append("clientID", userData._id);

      // Handle drawing file and number
      if (drawingFile) {
        // Add drawing number as JSON array (single element)
        // Don't convert to Number, keep as string
        formData.append("drawingNumbers", JSON.stringify([drawingNumber]));

        // Add drawing file
        formData.append("drawing", drawingFile);
      }

      // Debug log
      console.log("=== SENDING TO BACKEND ===");
      console.log("templateID:", selectedTemplate._id);
      console.log(
        "preferredSiteVisitDate:",
        JSON.stringify({
          fromDate: dateString,
          toDate: toDateString,
        })
      );
      console.log("specificInstructions:", JSON.stringify(instructions));
      console.log("drawingNumbers:", JSON.stringify([Number(drawingNumber)]));

      // Make API call
      const response = await axios.post(
        `${url}/api/client/req-checklist`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        showToast(
          "success",
          "Success!",
          response.data.message || "Site check request created successfully!"
        );

        console.log("=== BACKEND RESPONSE ===");
        console.log(
          "Stored data:",
          JSON.stringify(response.data.data, null, 2)
        );

        // Refresh user data to update credits in Navbar instantly
        refreshUserData();

        resetForm();

        // Navigate to All Projects after short delay
        setTimeout(() => {
          navigate("/projects/all");
        }, 1200);
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      if (error.response) {
        const { status, data } = error.response;

        console.error("Backend error:", data);

        if (status === 400) {
          showToast(
            "error",
            "Invalid Request",
            data.message || "Invalid request data. Please check your inputs."
          );
        } else if (status === 404) {
          showToast(
            "error",
            "Template Not Found",
            data.message || "Checklist template not found or inactive."
          );
        } else if (status === 401) {
          showToast(
            "error",
            "Authentication Failed",
            "Authentication failed. Please login again."
          );
        } else if (status === 403) {
          showToast(
            "error",
            "Permission Denied",
            "You don't have permission to perform this action."
          );
        } else if (status === 409) {
          showToast(
            "error",
            "Duplicate Request",
            data.message || "A similar request already exists."
          );
        } else if (status === 500) {
          showToast(
            "error",
            "Server Error",
            "Server error. Please try again later."
          );
        } else {
          showToast(
            "error",
            "Submission Failed",
            data.message || "Failed to submit site check request."
          );
        }
      } else if (error.request) {
        showToast(
          "error",
          "Connection Error",
          "No response from server. Please check your connection."
        );
      } else {
        showToast(
          "error",
          "Submission Failed",
          "Failed to submit site check request. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNameOfProject("");
    setDetailedAddress("");
    setNameOfClient("");
    setPhoneOfClient("");
    setUseMyDetails(false);
    setIsOutstation(false);
    setUserEnteredDetails({ name: "", phone: "" });
    setDateRange("");
    setStartDate(null);
    setEndDate(null);
    setAdditionalDetails("");
    setSelectedSite("");
    setInstructions([]);
    setCurrentInstruction("");
    setDrawingFile(null);
    setDrawingNumber("");
    try {
      localStorage.removeItem(draftKey);
    } catch (e) {
      console.error("Failed to clear draft", e);
    }
  };

  const datePickerOpen = Boolean(datePickerAnchor);
  const id = datePickerOpen ? "date-range-popover" : undefined;

  if (authLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box>
        <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, mb: 3 }}>
          Request a New Site Check:
        </Typography>
        <Box
          sx={{
            p: 2,
            backgroundColor: "#FFF8E1",
            borderRadius: 1,
            border: `1px solid #FFD54F`,
          }}
        >
          <Typography sx={{ color: COLORS.PRIMARY }}>
            You must be logged in to submit a site check request. Please login
            first.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Top Center Success Toaster Alert */}
        {purchaseToast && (
          <Box sx={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            width: { xs: '90%', sm: '550px' },
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            animation: 'slideDown 0.3s ease-out',
            '@keyframes slideDown': {
              from: { transform: 'translate(-50%, -50px)', opacity: 0 },
              to: { transform: 'translate(-50%, 0)', opacity: 1 }
            }
          }}>
            <Alert
              severity="success"
              icon={false}
              sx={{
                borderRadius: '12px',
                fontSize: '0.92rem',
                py: 2,
                px: 3,
                border: '1px solid #c8e6c9',
                bgcolor: '#f4fbf7',
                position: 'relative',
              }}
            >
              {/* Close button at top right */}
              <IconButton
                onClick={() => setPurchaseToast(null)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#2e7d32',
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#1b5e20', pr: 4 }}>
                  🎉 Congratulations! Payment Successful
                </Typography>
                <Typography variant="body2" sx={{ color: '#2e7d32', lineHeight: 1.5 }}>
                  You have successfully purchased the <strong>{purchaseToast.planName}</strong> plan.
                  {purchaseToast.credits ? ` ${purchaseToast.credits} credits have been added to your account.` : ""}
                  {purchaseToast.clientEmail ? ` We will share the invoice to ${purchaseToast.clientEmail} as well.` : ""}
                </Typography>
                {purchaseToast.purchaseId && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        window.open(`${url}/api/invoice/download/${userData._id}/${purchaseToast.purchaseId}?token=${token}`);
                        setPurchaseToast(null);
                      }}
                      sx={{
                        bgcolor: COLORS.PRIMARY,
                        color: '#ffffff',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3,
                        py: 0.75,
                        borderRadius: '6px',
                        '&:hover': { bgcolor: COLORS.SECONDARY || '#301352' }
                      }}
                    >
                      Download Invoice
                    </Button>
                  </Box>
                )}
              </Box>
            </Alert>
          </Box>
        )}

        <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, mb: 3 }}>
          Request a New Site Check:
        </Typography>

        <ClientNotificationBanner />

        <Paper sx={{ p: 1, backgroundColor: "transparent", boxShadow: "none" }}>
          {/* Row 1 */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Name of Project *
              </Typography>
              <TextField
                fullWidth
                placeholder="Name of Project"
                size="small"
                sx={inputStyles}
                value={nameOfProject}
                onChange={(e) => setNameOfProject(e.target.value)}
                required
              />
            </Box>

            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Detailed Address *
              </Typography>
              <TextField
                fullWidth
                placeholder="Detailed Address"
                size="small"
                sx={inputStyles}
                value={detailedAddress}
                onChange={(e) => setDetailedAddress(e.target.value)}
                required
              />
            </Box>
          </Box>

          <FormControlLabel
            sx={{ mt: 3, color: COLORS.PRIMARY, ...TYPOGRAPHY.H4 }}
            control={
              <Checkbox
                sx={{
                  color: COLORS.PRIMARY,
                  "&.Mui-checked": { color: COLORS.PRIMARY },
                }}
                checked={useMyDetails}
                onChange={handleUseMyDetailsChange}
              />
            }
            label="Use my Details for POC"
          />


          {/* Row 3 */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              mt: 3,
            }}
          >
            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Name of Contact Person at Project *
              </Typography>
              <TextField
                fullWidth
                placeholder={useMyDetails ? "" : "Contact Person Name"}
                size="small"
                sx={inputStyles}
                value={nameOfClient}
                onChange={(e) => {
                  if (!useMyDetails) {
                    setNameOfClient(e.target.value);
                  }
                }}
                required
                disabled={useMyDetails}
              />
            </Box>

            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Phone Number of POC *
              </Typography>
              <TextField
                fullWidth
                placeholder={useMyDetails ? "" : "10 digit phone number"}
                size="small"
                sx={inputStyles}
                value={phoneOfClient}
                onChange={(e) => {
                  if (!useMyDetails) {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      setPhoneOfClient(value);
                    }
                  }
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                required
                disabled={useMyDetails}
              />
            </Box>
          </Box>

          {/* Single Date Range Field with Calendar Icon */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              mt: 3,
            }}
          >
            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Site Visit Date Range *
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="YYYY-MM-DD to YYYY-MM-DD"
                value={dateRange}
                onChange={handleDateRangeChange}
                sx={inputStyles}
                onClick={handleCalendarClick}
                required
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleCalendarClick}
                        edge="end"
                        sx={{ color: COLORS.PRIMARY }}
                      >
                        <Icon name="calendar2" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Date Picker Popover */}
              <Popover
                id={id}
                open={datePickerOpen}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                PaperProps={{
                  sx: {
                    p: 2,
                    borderRadius: "5px",
                    border: `1px solid ${COLORS.PRIMARY}`,
                    mt: -1,
                    maxHeight: 400,
                    overflow: "visible",
                  },
                }}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                disableRestoreFocus={true}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    minWidth: 450,
                    position: "relative",
                  }}
                >
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Start Date"
                        sx={{
                          ...dateInputSx,
                          "& .MuiPickersPopper-paper": {
                            position: "absolute !important",
                            top: "auto !important",
                            bottom: "100% !important",
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small">
                                <Icon name="calendar2" color={COLORS.PRIMARY} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    PopperProps={{
                      placement: "top-start",
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -10],
                          },
                        },
                      ],
                    }}
                  />

                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    minDate={startDate || undefined}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="End Date"
                        disabled={!startDate}
                        sx={{
                          ...dateInputSx,
                          "& .MuiPickersPopper-paper": {
                            position: "absolute !important",
                            top: "auto !important",
                            bottom: "100% !important",
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" disabled={!startDate}>
                                <Icon
                                  name="calendar2"
                                  color={
                                    !startDate
                                      ? COLORS.LIGHT_SECONDARY
                                      : COLORS.PRIMARY
                                  }
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    PopperProps={{
                      placement: "top-start",
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -10],
                          },
                        },
                      ],
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleClearDates}
                    sx={{ color: COLORS.ERROR }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleDatePickerClose}
                    sx={{ bgcolor: COLORS.PRIMARY }}
                  >
                    Apply
                  </Button>
                </Box>
              </Popover>
            </Box>

            {/* Additional Details Textbox */}
            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Additional Time /Instructions
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Additional Time /Instructions"
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                sx={{
                  ...inputStyles,
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    borderRadius: "5px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Divider */}
          <Box sx={{ height: 1, bgcolor: COLORS.LIGHT_SECONDARY, my: 4 }} />

          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>
            Select Site Checklist from Dropdown
          </Typography>

          <Box
            sx={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 3 }}
          >
            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Choose Site Check *
              </Typography>
              <Autocomplete
                size="small"
                options={templateOptions}
                value={selectedSite}
                onChange={(event, newValue) => setSelectedSite(newValue || "")}
                loading={templatesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      templatesLoading
                        ? "Loading templates..."
                        : "Search site check..."
                    }
                    sx={inputStyles}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {templatesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                PaperComponent={({ children }) => (
                  <Paper
                    sx={{
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 8,
                        right: 6,
                        width: "2px",
                        height: "calc(100% - 16px)",
                        backgroundColor: COLORS.LIGHT_SECONDARY,
                        borderRadius: "2px",
                      },
                      "& .MuiAutocomplete-listbox": {
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        "-ms-overflow-style": "none",
                        "scrollbar-width": "none",
                      },
                    }}
                  >
                    {children}
                  </Paper>
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props} key={option}>
                    {option}
                  </MenuItem>
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: 200,
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    "-ms-overflow-style": "none",
                    "scrollbar-width": "none",
                  },
                }}
              />
              {templateOptions.length === 0 && !templatesLoading && (
                <Typography
                  sx={{ fontSize: 12, color: COLORS.LIGHT_SECONDARY, mt: 0.5 }}
                >
                  No active checklist templates available
                </Typography>
              )}
            </Box>

            <Box>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}
              >
                Specific Instructions
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add Specific Instructions"
                    value={currentInstruction}
                    onChange={(e) => setCurrentInstruction(e.target.value)}
                    sx={inputStyles}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Icon name="plus2" />}
                    sx={{
                      bgcolor: COLORS.SECONDARY,
                      borderRadius: 2,
                      px: 2,
                      height: "40px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleAddInstruction}
                  >
                    Add
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  {instructions.map((item, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1.2,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        width: "100%",          // 🔹 Fix width to container
                        maxWidth: "100%",
                        backgroundColor: "#fff",
                        overflow: "hidden",     // 🔹 Prevent overflow
                      }}
                    >
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          flex: 1,                       // 🔹 Take available space only
                          whiteSpace: "normal",          // 🔹 Allow wrapping
                          wordBreak: "break-word",       // 🔹 Break very long words
                          overflowWrap: "anywhere",      // 🔹 Modern wrap support
                        }}
                      >
                        {item}
                      </Typography>

                      <IconButton
                        onClick={() => handleRemoveInstruction(index)}
                        size="small"
                        sx={{ flexShrink: 0 }}            // 🔹 Prevent icon from shrinking
                      >
                        <Icon name="close2" color={COLORS.ERROR} />
                      </IconButton>
                    </Paper>

                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Drawing Upload Section - Single File */}
          <Box sx={{ mt: 3 }}>
            <Typography
              sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.4 }}
            >
              Drawing Upload (PDF only) *
            </Typography>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}
            >
              {/* PDF Upload Box */}
              <Box>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.PRIMARY,
                    mb: 1,
                  }}
                >
                  Upload Drawing PDF
                </Typography>

                {!drawingFile ? (
                  <Button
                    component="label"
                    variant="outlined"
                    fullWidth
                    sx={{
                      height: 40,
                      justifyContent: "flex-start",
                      border: `2px dashed ${COLORS.PRIMARY}`,
                      backgroundColor: "#f9f9f9",
                      px: 2,
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                        border: `2px dashed ${COLORS.SECONDARY}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <Icon name="upload" size={18} color={COLORS.PRIMARY} />
                      <Typography
                        sx={{
                          color: COLORS.PRIMARY,
                          fontWeight: 500,
                          fontSize: 13,
                        }}
                      >
                        Click to upload PDF
                      </Typography>
                    </Box>

                    <input
                      hidden
                      type="file"
                      accept="application/pdf"
                      onChange={handleDrawingFileChange}
                    />
                  </Button>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      height: 40,
                      px: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Icon name="filePdf" size={16} color={COLORS.ERROR} />
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.PRIMARY,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {drawingFile.name}
                      </Typography>
                    </Box>

                    <IconButton size="small" onClick={handleRemoveDrawing}>
                      <Icon name="close2" color={COLORS.ERROR} />
                    </IconButton>
                  </Paper>
                )}
              </Box>

              {/* Drawing Number Input */}
              <Box>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.PRIMARY,
                    mb: 1,
                  }}
                >
                  Drawing Number *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Drawing Number"
                  value={drawingNumber}
                  onChange={(e) => setDrawingNumber(e.target.value)}
                  sx={inputStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon name="hashTag" color={COLORS.PRIMARY} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Outstation Checkbox - Moved to end */}
          <FormControlLabel
            sx={{ mt: 3, ml: -1, color: COLORS.PRIMARY, ...TYPOGRAPHY.H4, display: 'flex' }}
            control={
              <Checkbox
                sx={{
                  color: COLORS.PRIMARY,
                  "&.Mui-checked": { color: COLORS.PRIMARY },
                }}
                checked={isOutstation}
                onChange={(e) => setIsOutstation(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                  Is this an Outstation Visit?
                </Typography>
                <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.LIGHT_SECONDARY }}>
                  (Deducts 1 Outstation Credit. Outstation visits take more time and resources)
                </Typography>
              </Box>
            }
          />

          {/* Submit */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: COLORS.SECONDARY,
                borderRadius: 3,
                px: 3,
                py: 1.2,
                textTransform: "none",
                minWidth: 200,
              }}
              onClick={handleSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Icon name="downArrow" />
                )
              }
              disabled={loading || templatesLoading || !isAuthenticated}
            >
              {loading ? "Submitting..." : "Request for Site Check Visit"}
            </Button>
          </Box>
        </Paper>

        <AppToast
          open={toastOpen}
          type={toastType}
          title={toastTitle}
          message={toastMessage}
          onClose={handleToastClose}
          autoHideDuration={5000}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AddSiteCheck;
